import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { fetchHistory, historySelector } from "./historySlice";
import { Sparkles, History as HistoryIcon, ArrowRight, Calendar, BookOpen } from "lucide-react";

interface HistoryItem {
  id: string;
  evaluation_id: string;
  file_name: string;
  date: string;
  score: number;
  total_questions: number;
  total_score?: number;
  max_score?: number;
  exam_type?: string;
  subject?: string;
  duration?: string;
  subjects?: { name: string; score: number }[];
}

const History = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { history, isFetching } = useAppSelector(historySelector);

  const [selectedFilter, setSelectedFilter] = useState("All Papers");

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch history when filter changes
  useEffect(() => {
    dispatch(fetchHistory(selectedFilter === "All Papers" ? undefined : selectedFilter));
  }, [dispatch, selectedFilter]);

  // Handle filter change
  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter);
  };

  const displayHistory = history.filter((item: { subject: string; }) => {
    if (selectedFilter === "All Papers") return true;
    return item.subject === selectedFilter;
  });

  const filterOptions = ["All Papers", "GS1", "GS2", "GS3", "GS4", "ESSAY"];

  // Colors for different paper types
  const paperColors: Record<string, string> = {
    "GS1": "text-blue-600 bg-blue-50 border-blue-200",
    "GS2": "text-purple-600 bg-purple-50 border-purple-200",
    "GS3": "text-emerald-600 bg-emerald-50 border-emerald-200",
    "GS4": "text-amber-600 bg-amber-50 border-amber-200",
    "ESSAY": "text-rose-600 bg-rose-50 border-rose-200",
    "All Papers": "text-indigo-600 bg-indigo-50 border-indigo-200"
  };

  const getPercentageColor = (percentage: number) => {
    if (percentage > 50) return "text-emerald-600 bg-emerald-500";
    if (percentage > 30) return "text-amber-600 bg-amber-500";
    return "text-red-600 bg-red-500";
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  if (isFetching && history.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mx-auto" />
          <p className="text-gray-500 font-medium">Loading your history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 font-sans text-gray-900">

      {/* Header / Hero */}
      <div className=" relative overflow-hidden">

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
          >
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 border border-indigo-100 rounded-full text-xs font-bold text-indigo-600 uppercase tracking-widest mb-4">
                <Sparkles className="w-3 h-3" />
                Performance Archive
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight leading-tight mb-4">
                Your Evaluation <span className="text-indigo-600">Journey</span>
              </h1>
              <p className="text-lg text-gray-500 max-w-xl font-medium">
                Track your progress, analyze your scores, and revisit past feedback to continuously improve.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">

        {/* Tabs Filter */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 overflow-x-auto pb-4 no-scrollbar -mx-4 px-4 md:px-0"
        >
          <div className="flex gap-2">
            {filterOptions.map((filter) => (
              <button
                key={filter}
                onClick={() => handleFilterChange(filter)}
                disabled={isFetching}
                className={`
                   px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-300 border
                   ${selectedFilter === filter
                    ? "bg-gradient-to-r from-indigo-600 to-indigo-700 text-white border-black"
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-900"
                  }
                `}
              >
                {filter}
              </button>
            ))}
          </div>
        </motion.div>

        {/* History Grid */}
        {displayHistory.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-12 text-center shadow-sm border border-gray-100 max-w-lg mx-auto"
          >
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <HistoryIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No evaluations found</h3>
            <p className="text-gray-500 mb-8 max-w-xs mx-auto text-sm">
              We couldn't find any {selectedFilter === "All Papers" ? "evaluations" : selectedFilter}. Start a new one to see it here!
            </p>
            <Button
              className="bg-black hover:bg-gray-800 text-white rounded-full h-11 px-6 text-sm font-bold transition-all shadow-lg shadow-gray-200"
              onClick={() => navigate("/evaluate")}
            >
              Start New Evaluation
            </Button>
          </motion.div>
        ) : (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            {displayHistory.map((item: HistoryItem) => {
              const max = item.max_score || 0;
              const percentage = max > 0 ? (item.score / max) * 100 : 0;
              const colorClass = paperColors[item.subject || "GS1"] || paperColors["All Papers"];

              return (
                <motion.div
                  key={item.id || item.evaluation_id}
                  variants={fadeInUp}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  <div
                    className="h-full bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-indigo-500/10 transition-all cursor-pointer overflow-hidden flex flex-col group"
                    onClick={() => navigate(`/evaluation/${item.evaluation_id || item.id}`)}
                  >

                    {/* Card Header */}
                    <div className="p-6 md:p-8 flex-1">
                      <div className="flex justify-between items-start mb-6">
                        <div className={`px-3 py-1 rounded-full text-xs font-bold border uppercase tracking-wide ${colorClass}`}>
                          {item.subject || "UPSC"}
                        </div>
                        <div className="flex items-center text-xs font-bold text-gray-400 gap-1.5 bg-gray-50 px-2 py-1 rounded-md">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </div>
                      </div>

                      {/* Score Badge */}
                      <div className="mb-8">
                        <div className="flex items-baseline gap-1.5 mb-2">
                          <span className={`text-4xl md:text-5xl font-black tracking-tight ${getPercentageColor(percentage).split(' ')[0]}`}>{item.score.toFixed(1)}</span>
                          <span className="text-base font-bold text-gray-400">/ {item.max_score}</span>
                        </div>
                        {/* Progress Bar */}
                        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: `${percentage}%` }}
                            viewport={{ once: true }}
                            className={`h-full rounded-full ${getPercentageColor(percentage).split(' ')[1]}`}
                          />
                        </div>
                      </div>


                    </div>

                    {/* Footer */}
                    <div className="px-6 md:px-8 py-4 border-t border-gray-100 bg-gray-50/50 flex group-hover:bg-indigo-50/30 transition-colors items-center justify-between">
                      <span className="flex items-center gap-2 text-xs font-bold text-gray-500 group-hover:text-indigo-600 transition-colors">
                        <BookOpen className="w-3.5 h-3.5" />
                        View Full Analysis
                      </span>
                      <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full hover:bg-white hover:text-indigo-600 group-hover:translate-x-1 transition-all">
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>

                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default History;
